import React, {memo} from "react";
import {ButtonProps} from "./button.component";
import clsx from "clsx";

type Variant = "solid" | "outline" | "ghost";
type Size = "sm" | "md" | "lg";

interface BaseProps {
    "aria-label"?: string;
    variant?: Variant;
    size?: Size;
    loading?: boolean;
}
interface IconButtonProps extends BaseProps {
    component?: React.ElementType;
    children: React.ReactNode;
    [key: string]: any;
}

const sizeMap: Record<Size, string> = {
    sm: "h-8 w-8 text-sm",
    md: "h-10 w-10 text-base",
    lg: "h-12 w-12 text-lg",
};
const variantMap: Record<Variant, string> = {
    solid: "bg-black text-white hover:bg-black/90",
    outline: "bg-white border border-gray-200 hover:bg-gray-50",
    ghost: "bg-transparent hover:bg-gray-100",
};

export const IconButtonComponent: React.FC<IconButtonProps> = memo(({
                                                                        children,
                                                                        variant = "solid",
                                                                        size = "md",
                                                                        loading = false,
                                                                        className = "",
                                                                        component: Component = "button",
                                                                        ...props
                                                                    }) => {
    const isDisabled =
        Component === "button" ? (props as ButtonProps).disabled || loading : undefined;

    return (
        <Component
            {...props}
            aria-label={props["aria-label"]}
            {...(Component === "button" ? { disabled: isDisabled } : {})}
            className={clsx(
                "inline-flex items-center justify-center rounded-full focus:outline-none focus:ring-2 rb-icon focus:ring-offset-2 focus:ring-black/20 transition-shadow",
                sizeMap[size],
                variantMap[variant],
                {
                    "opacity-60 cursor-not-allowed": isDisabled,
                    "shadow-sm": !isDisabled,
                },
                className
            )}
        >
            {loading ? (
                <svg
                    className="animate-spin w-4 h-4"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                >
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                    ></path>
                </svg>
            ) : (
                <span className="flex items-center justify-center">{children}</span>
            )}
        </Component>
    );
});
